import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Eye, Mail, Calendar, User, ChevronLeft, ChevronRight, CreditCard, Shield, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ListingDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    fetchListing();
    checkPaymentStatus();
  }, [id]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`${API_URL}/listings/${id}`);
      setListing(response.data);
    } catch (error) {
      console.error('Error fetching listing:', error);
      navigate('/marketplace');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get('session_id');
    const paymentResult = searchParams.get('payment');

    if (paymentResult === 'cancelled') {
      setPaymentStatus('cancelled');
      return;
    }

    if (sessionId && token) {
      // Poll for payment status
      try {
        const response = await axios.get(`${API_URL}/payments/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (response.data.payment_status === 'paid') {
          setPaymentStatus('success');
          toast.success(t('payment.success'));
          fetchListing(); // Refresh to show sold status
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
    }
  };

  const handleBuyNow = async () => {
    if (!token) {
      navigate('/login');
      return;
    }

    setPaymentLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/payments/checkout`,
        {
          listing_id: id,
          origin_url: window.location.origin,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      const message = error.response?.data?.detail || t('common.error');
      toast.error(message);
    } finally {
      setPaymentLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      motorcycle: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
      parts: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      accessories: 'bg-green-500/10 text-green-500 border-green-500/20',
      gear: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
    };
    return colors[category] || 'bg-muted text-muted-foreground';
  };

  const images = listing?.images?.length > 0 
    ? listing.images 
    : ['https://images.unsplash.com/photo-1629628083289-009e6f87f5cf?crop=entropy&cs=srgb&fm=jpg&q=85'];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const commission = listing ? listing.price * 0.10 : 0;
  const total = listing ? listing.price + commission : 0;

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-32 mb-6" />
          <div className="aspect-video bg-muted rounded-lg mb-6" />
          <div className="h-8 bg-muted rounded w-3/4 mb-4" />
          <div className="h-6 bg-muted rounded w-1/4 mb-6" />
          <div className="h-24 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (!listing) return null;

  const isSold = listing.status === 'sold';
  const isOwner = user?.id === listing.seller_id;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/marketplace')}
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        data-testid="back-to-marketplace"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('marketplace.title')}
      </button>

      {/* Payment Status Alerts */}
      {paymentStatus === 'success' && (
        <Alert className="mb-6 border-green-500/50 bg-green-500/10">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <AlertDescription className="text-green-500">
            {t('payment.success')}
          </AlertDescription>
        </Alert>
      )}

      {paymentStatus === 'cancelled' && (
        <Alert className="mb-6 border-yellow-500/50 bg-yellow-500/10">
          <XCircle className="h-4 w-4 text-yellow-500" />
          <AlertDescription className="text-yellow-500">
            {t('payment.cancelled')}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Image Gallery */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative"
          >
            <div 
              className="aspect-[4/3] rounded-lg bg-cover bg-center"
              style={{ backgroundImage: `url('${images[currentImageIndex]}')` }}
              data-testid="listing-main-image"
            />
            
            {images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"
                  data-testid="prev-image-btn"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"
                  data-testid="next-image-btn"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === currentImageIndex ? 'bg-primary' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}

            <div className="absolute top-4 left-4 flex gap-2">
              <Badge className={getCategoryColor(listing.category)}>
                {t(`marketplace.categories.${listing.category}`)}
              </Badge>
              <Badge className="bg-black/60 text-white border-0">
                {t(`marketplace.conditions.${listing.condition}`)}
              </Badge>
              {isSold && (
                <Badge className="bg-red-500 text-white border-0">
                  {t('payment.sold')}
                </Badge>
              )}
            </div>
          </motion.div>

          {/* Title & Price */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h1 className="font-heading font-bold text-2xl md:text-3xl uppercase tracking-tight">
              {listing.title}
            </h1>
            <p className="text-primary font-mono font-bold text-3xl mt-2">
              {listing.price.toLocaleString()} {listing.currency}
            </p>
          </motion.div>

          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <CardContent className="p-6">
                <h2 className="font-heading font-bold text-lg uppercase mb-4">Description</h2>
                <p className="text-muted-foreground whitespace-pre-line">{listing.description}</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Buy Now Card */}
          {!isOwner && !isSold && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              <Card className="border-primary/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4 text-primary">
                    <Shield className="w-5 h-5" />
                    <span className="font-medium">{t('payment.securePayment')}</span>
                  </div>
                  
                  <div className="space-y-2 mb-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{i18n.language === 'fr' ? 'Prix' : 'Price'}</span>
                      <span>{listing.price.toLocaleString()} €</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('payment.commission')} (10%)</span>
                      <span>{commission.toLocaleString(undefined, { minimumFractionDigits: 2 })} €</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg pt-2 border-t border-border">
                      <span>{t('payment.total')}</span>
                      <span className="text-primary">{total.toLocaleString(undefined, { minimumFractionDigits: 2 })} €</span>
                    </div>
                  </div>

                  <Button 
                    className="w-full brand-glow gap-2 h-12"
                    onClick={handleBuyNow}
                    disabled={paymentLoading}
                    data-testid="buy-now-btn"
                  >
                    <CreditCard className="w-5 h-5" />
                    {paymentLoading ? t('payment.processing') : t('payment.buyNow')}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Sold Notice */}
          {isSold && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              <Card className="border-red-500/50 bg-red-500/10">
                <CardContent className="p-6 text-center">
                  <CheckCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
                  <p className="font-heading font-bold text-xl uppercase text-red-500">
                    {t('payment.sold')}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Seller Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card>
              <CardContent className="p-6">
                <h2 className="font-heading font-bold text-lg uppercase mb-4">
                  {i18n.language === 'fr' ? 'Vendeur' : 'Seller'}
                </h2>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{listing.seller_name}</p>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {listing.location}
                    </p>
                  </div>
                </div>

                <Dialog open={contactOpen} onOpenChange={setContactOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full gap-2" data-testid="contact-seller-btn">
                      <Mail className="w-4 h-4" />
                      {t('marketplace.contact')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-heading font-bold text-xl uppercase">
                        {t('marketplace.contact')}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                      <p className="text-muted-foreground mb-4">
                        {i18n.language === 'fr' 
                          ? `Pour contacter ${listing.seller_name}, envoyez un message depuis votre compte.`
                          : `To contact ${listing.seller_name}, send a message from your account.`}
                      </p>
                      <div className="p-4 bg-muted rounded-lg">
                        <p className="font-medium">{listing.seller_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {i18n.language === 'fr' ? 'Membre Moto Social' : 'Moto Social Member'}
                        </p>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </motion.div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card>
              <CardContent className="p-6">
                <h2 className="font-heading font-bold text-lg uppercase mb-4">
                  {i18n.language === 'fr' ? 'Détails' : 'Details'}
                </h2>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{i18n.language === 'fr' ? 'Catégorie' : 'Category'}</span>
                    <span>{t(`marketplace.categories.${listing.category}`)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{i18n.language === 'fr' ? 'État' : 'Condition'}</span>
                    <span>{t(`marketplace.conditions.${listing.condition}`)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('profile.country')}</span>
                    <span>{t(`countries.${listing.country}`)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Eye className="w-3 h-3" /> {t('marketplace.views')}
                    </span>
                    <span>{listing.views}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {i18n.language === 'fr' ? 'Publié' : 'Posted'}
                    </span>
                    <span>
                      {format(new Date(listing.created_at), 'PP', { 
                        locale: i18n.language === 'fr' ? fr : enUS 
                      })}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ListingDetail;
